import { Link } from '@tanstack/react-router';
import { ChevronDown, LogOut, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { shellCapabilities, useAuth } from '@/shared/auth';
import { BottomSheet } from '@/shared/components';
import { useObjectScope } from '@/shared/object-scope';
import { accountIdentity } from './AccountMenu';
import { CommandBar } from './CommandBar';
import { ShellLogo } from './NavEntryLink';
import { visibleAccountEntries } from './navVisibility';
import { ObjectScopeList } from './ObjectScopeList';
import { useObjectListMissing } from './ObjectScopeStatus';
import { WorkspaceRow } from './WorkspaceRow';

function ObjectPill() {
  const scope = useObjectScope();
  const [open, setOpen] = useState(false);
  const missing = useObjectListMissing();
  const name = missing ? 'Objektliste fehlt' : (scope.group?.name ?? 'Alle erlaubten Objekte');
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Objekt: ${name}`}
        className="rounded-pill flex h-11 min-w-0 flex-1 items-center gap-2 border border-white/20 bg-white/[0.06] px-3.5 text-[13px] font-semibold text-white"
      >
        {missing && (
          <TriangleAlert size={15} aria-hidden className="text-amber-on-dark flex-none" />
        )}
        <span className="min-w-0 flex-1 truncate text-left">{name}</span>
        <ChevronDown size={14} aria-hidden />
      </button>
      <BottomSheet open={open} onOpenChange={setOpen} title="Objekt">
        <ObjectScopeList onPicked={() => setOpen(false)} />
      </BottomSheet>
    </>
  );
}

function AccountSheetButton({ onLogout }: { onLogout: () => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { initials, roleLabel } = accountIdentity(user?.email, user?.roles);
  const close = () => setOpen(false);
  return (
    <>
      <button
        type="button"
        aria-label="Konto"
        onClick={() => setOpen(true)}
        className="text-forest-deep rounded-pill flex size-11 flex-none items-center justify-center bg-white text-[13px] font-bold"
      >
        {initials}
      </button>
      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        title={user?.email ?? 'Konto'}
        description={roleLabel}
      >
        <div className="flex flex-col gap-2 pb-2">
          <WorkspaceRow />
          {visibleAccountEntries(user).map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              onClick={close}
              className="bg-paper text-ink rounded-control flex min-h-[52px] items-center px-3 text-[15px] font-bold no-underline"
            >
              {entry.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => {
              close();
              onLogout();
            }}
            className="border-hairline text-sealwax rounded-control flex min-h-12 items-center justify-center gap-2 border bg-white text-[15px] font-bold"
          >
            <LogOut size={18} aria-hidden />
            Abmelden
          </button>
        </div>
      </BottomSheet>
    </>
  );
}

/** Handy-Kopf in Wald: Zeichen, Objektwahl, Suche und Konto (Board „Gerüst · Handy“). */
export function PhoneTopBar({ onLogout }: { onLogout: () => void }) {
  const { user } = useAuth();
  const capabilities = shellCapabilities(user);
  return (
    <header className="on-forest bg-forest-deep flex flex-none items-center gap-2.5 px-4 py-3 text-white">
      <ShellLogo size={36} />
      {capabilities.objectScope ? (
        <ObjectPill />
      ) : (
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-mint font-mono text-[10px] font-semibold uppercase tracking-[0.14em]">
            Workspace
          </span>
          <span className="truncate text-sm font-bold">{user?.workspaceName ?? 'StayOn'}</span>
        </span>
      )}
      {capabilities.commandBar && <CommandBar variant="phone" />}
      <AccountSheetButton onLogout={onLogout} />
    </header>
  );
}
